import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';

import { BarStack, LinePath } from '@visx/shape';
import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';

import {
  getParams,
  getMonth,
  thousandFormat,
  thousandFormat2,
  getMonths2,
  getMonths,
  printFormat
} from '../../../../Utils/Functions';
import {
  enMonths,
  positiveActiveColor,
  positiveDisableColor,
  negativeActiveColor,
  negativeDisableColor,
  activeLabelColor,
} from '../../../../Assets/js/constant';

// accessors
const x = (d) => d.month;

let tooltipTimeout;
const MIN_VISIBLE_BAR_HEIGHT = 2;

const BottomChart = ({
  bottomData,
  selectedYears,
  selectedTopItems,
  selectedBottomItems,
  updateFilter,
  cashflowMiddleRequest,
  defaultStartMonth,
  defaultMonth,
}) => {
  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState([]);
  const [colors, setColors] = useState([]);
  const [bottomOffset, setBottomOffset] = useState(0);

  const isMountedRef = useRef(true);

  // Force re-render when window is resized
  const onResize = useCallback(() => {
    if (isMountedRef.current) {
      setResize(prev => !prev);
    }
  }, []);

  const handlePos = useCallback((event) => {
    const _pos =
      event.currentTarget.getBoundingClientRect().top + window.scrollY;
    if (bottomOffset !== _pos && isMountedRef.current) {
      setBottomOffset(_pos);
    }
  }, [bottomOffset]);

  const prepareData = useCallback(() => {
    let dictData = {};
    let dictTotals = {};
    let dataArray = [];
    let totalsArray = [];

    let colorsArray = selectedYears.reduce((ret, year) => {
      ret.push(positiveActiveColor);
      return ret;
    }, []);

    let fiscalMonths = getMonths(defaultStartMonth);
    let fiscalMonths2 = getMonths2(defaultMonth, defaultStartMonth);
    
    fiscalMonths.forEach((month) => {
      dictData[month] = {};
      dictData[month]['month'] = month;
      dictData[month]['value'] = {};

      selectedYears.forEach((year) => {
        dictData[month][year] = 0;
      });

      dictTotals[month] = 0;
    });

    if (bottomData !== undefined)
      bottomData.forEach((item) => {
        let month = getMonth(item.Date);
        if (dictData[month] !== undefined) {
          if (
            fiscalMonths2.indexOf(month) > -1 &&
            item.FY === selectedYears[0]
          ) {
            dictData[month][item.FY] += parseFloat(item.CashBalance);
            dictData[month]['value'][item.FY] = item;
            dictTotals[month] += parseFloat(item.CashBalance);
          }
        }
      });

    fiscalMonths.forEach((month) => {
      dataArray.push(dictData[month]);
      totalsArray.push(dictTotals[month]);
    });

    setData(dataArray);
    setTotals(totalsArray);
    setColors(colorsArray);
  }, [bottomData, selectedYears, defaultStartMonth, defaultMonth]);

  const getColor = useCallback((bar) => {
    const year = bar.key;
    const month = bar.bar.data.month;

    const actual = bar.bar.data[year] ? bar.bar.data[year] : 0;
    const activeColor = actual < 0 ? negativeActiveColor : positiveActiveColor;
    const disableColor =
      actual < 0 ? negativeDisableColor : positiveDisableColor;

    if (selectedBottomItems.length === 0) {
      return activeColor;
    }
    if (selectedBottomItems.indexOf(month) > -1) return activeColor;

    for (let i = 0; i < selectedBottomItems.length; i++) {
      if (
        selectedBottomItems[i].month === month &&
        selectedBottomItems[i].year === year
      ) {
        return activeColor;
      }
    }

    return disableColor;
  }, [selectedBottomItems]);

  const handleBar = useCallback((event, bar) => {
    let _selectedBars, _selectedBottomItems;

    const month = bar.bar.data.month;
    const year = bar.key;

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedBottomItems = selectedBottomItems.slice();

      let index = -1;
      for (let i = 0; i < selectedBars.length; i++) {
        if (selectedBars[i] === event.target) {
          index = i;
          break;
        }
      }

      if (index === -1) {
        event.target.classList.add('barActive');
        _selectedBars.push(event.target);
        _selectedBottomItems.push({ month: month, year: year });
      } else {
        event.target.classList.remove('barActive');
        _selectedBars.splice(index, 1);
        _selectedBottomItems.splice(index, 1);
      }
    } else {
      _selectedBottomItems = [];
      let exist = false;

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });

      if (exist && selectedBars.length === 1) {
        _selectedBottomItems = [];
        _selectedBars = [];
      } else {
        _selectedBottomItems = [{ month: month, year: year }];
        _selectedBars = [event.target];
        event.target.classList.add('barActive');
      }
    }

    let dimDate = [];
    _selectedBottomItems.forEach((item, key) => {
      let _DimDate = printFormat.dateYYYYMMDD(
        item.month + 1 >= defaultStartMonth
          ? item.year - 1
          : item.year,
        item.month + 1,
      );
      dimDate.push(_DimDate);
    });

    setSelectedBars(_selectedBars);

    updateFilter({
      selectedBottomItems: _selectedBottomItems,
      dimDate: dimDate,
      selectedTopItems: [],
    });

    cashflowMiddleRequest(dimDate);
  }, [selectedBars, selectedBottomItems, defaultStartMonth, updateFilter, cashflowMiddleRequest]);

  const handleLabel = useCallback((event, month) => {
    let _selectedBottomItems, _selectedBars;
    
    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedBottomItems = selectedBottomItems.slice();

      let index = -1;
      for (let i = 0; i < selectedBottomItems.length; i++) {
        if (selectedBottomItems[i].month === month) {
          index = i;
          break;
        }
      }

      if (index === -1) {
        _selectedBottomItems.push({
          month: month,
          year: selectedYears[0]
        });
      } else {
        _selectedBottomItems.splice(index, 1);
      }
    } else {
      _selectedBars = [];
      _selectedBottomItems = [];
      let exist = false;

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });

      if (exist && selectedBottomItems.length === 1) {
        _selectedBottomItems = [];
        _selectedBars = [];
      } else {
        _selectedBottomItems = [{
          month: month,
          year: selectedYears[0]
        }];
        _selectedBars = [event.target];
      }
    }

    let dimDate = [];
    _selectedBottomItems.forEach((item, key) => {
      let _DimDate = printFormat.dateYYYYMMDD(
        item.month + 1 >= defaultStartMonth
          ? item.year - 1
          : item.year,
        item.month + 1,
      );
      dimDate.push(_DimDate);
    });

    setSelectedBars(_selectedBars);

    updateFilter({
      selectedBottomItems: _selectedBottomItems,
      dimDate: dimDate,
      selectedTopItems: [],
    });

    cashflowMiddleRequest(dimDate);
  }, [selectedBars, selectedBottomItems, selectedYears, defaultStartMonth, updateFilter, cashflowMiddleRequest]);

  const deSelectAll = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');

    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    setHoverBar(null);
    setSelectedBars([]);
    updateFilter({
      selectedBottomItems: [],
    });
  }, [hoverBar, selectedBars, updateFilter]);

  const showTooltipHandler = useCallback((event, xScale, bar, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);

    let dataObj = {},
      top,
      left;

    top = event.nativeEvent.layerY + 30;
    left =
      window.innerWidth / 2 < event.nativeEvent.layerX
        ? event.nativeEvent.layerX - 250
        : event.nativeEvent.layerX + 50;

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);

      dataObj = bar;
    } else {
      dataObj['forecast'] = bar;
    }

    dataObj['isBar'] = isBar;

    showTooltip({
      tooltipData: dataObj,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [hoverBar, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);
      hideTooltip();
    }, 300);
  }, [hoverBar, hideTooltip]);

  // Effects
  useEffect(() => {
    isMountedRef.current = true;
    prepareData();
    window.addEventListener('resize', onResize);
    
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', onResize);
    };
  }, [onResize, prepareData]);

  useEffect(() => {
    if (isMountedRef.current) {
      prepareData();
    }
  }, [bottomData, selectedTopItems, bottomOffset, prepareData]);

  // Render logic
  let xTransform = 0;
  let maxYValue = Math.max(
    Math.abs(Math.min(Math.min(...totals))),
    Math.max(Math.max(...totals)),
  );

  if (
    !isFinite(maxYValue) ||
    maxYValue === Number.NEGATIVE_INFINITY ||
    isNaN(maxYValue) ||
    maxYValue === 0
  )
    xTransform = 30;
  else {
    if (Math.abs(Math.min(Math.min(...totals))) === maxYValue) {
      if (parseInt(thousandFormat(maxYValue)[0]) > 7)
        xTransform = 7.5 * (thousandFormat2(maxYValue).length + 2) + 5;
      else xTransform = 7.5 * (thousandFormat2(maxYValue).length + 1) + 5;
    } else {
      if (parseInt(thousandFormat(maxYValue)[0]) > 7)
        xTransform = 7.5 * (thousandFormat2(maxYValue).length + 1) + 5;
      else xTransform = 7.5 * thousandFormat2(maxYValue).length + 5;
    }
  }

  const width = window.innerWidth;
  const height = 150;
  const margin = {
    top: 10,
    right: 0,
    bottom: 20,
    left: xTransform,
  };

  const { pageYOffset, xMax, yMax } = getParams(
    window,
    width,
    height,
    margin,
  );

  // scales
  const xScale = scaleBand()
    .domain(data.map(x))
    .rangeRound([0, xMax])
    .padding(0.2);
    
  const yScale = scaleLinear({
    domain:
      totals.length === 0
        ? [0, 100]
        : [Math.min(0, Math.min(...totals)), Math.max(0, Math.max(...totals) * 1.2)],
    range: [yMax, 0],
    nice: true,
  });

  const color = scaleOrdinal({
    domain: selectedYears,
    range: colors,
  });

  let line_data = [];
  line_data.push({ x: 0, y: yScale(0) });
  line_data.push({ x: xMax, y: yScale(0) });

  return (
    <div
      id="rootPos"
      onMouseMove={handlePos}
      onTouchMove={handlePos}
    >
      <div className="well" style={{ maxHeight: `${height + 35}px` }}>
        <div className="subtitle mb-10">Change in Cash Balance</div>

        <Grid container>
          <svg width={width} height={height}>
            <rect
              x={0}
              y={0}
              width={width}
              height={height}
              fill="transparent"
              onClick={deSelectAll}
            />

            <Group top={margin.top} left={margin.left}>
              <LinePath
                data={line_data}
                x={(nx) => (nx.x ? nx.x : 0)}
                y={(nx) => (nx.y ? nx.y : 0)}
                stroke="#555555"
                strokeDasharray="6,2"
              />

              <BarStack
                data={data}
                keys={selectedYears}
                x={x}
                xScale={xScale ? xScale : 0}
                yScale={yScale ? yScale : 0}
                color={color}
              >
                {(barStacks) => {
                  return barStacks.map((barStack) => {
                    return barStack.bars.map((bar) => {
                      const barValue = Number(bar.bar.data[bar.key] || 0);
                      const zeroY = yScale(0);

                      if (isNaN(bar.height)) {
                        bar.y = 0;
                        bar.height = 0;
                      } else {
                        if (bar.height < 0) {
                          if (bar.y < zeroY)
                            bar.height = -bar.y + zeroY;
                          else bar.height = bar.y - zeroY;

                          bar.y = zeroY;
                        }

                        // A valid month can net close to zero after all cashflow
                        // categories are summed. Without a minimum rendered size
                        // that bar becomes sub-pixel and looks like missing data.
                        if (barValue !== 0 && bar.height > 0 && bar.height < MIN_VISIBLE_BAR_HEIGHT) {
                          bar.height = MIN_VISIBLE_BAR_HEIGHT;
                          bar.y = barValue < 0 ? zeroY : zeroY - MIN_VISIBLE_BAR_HEIGHT;
                        }
                      }
                      return (
                        <rect
                          key={`bar-income-${barStack.index}-${bar.index}`}
                          x={bar.x ? bar.x : 0}
                          y={bar.y ? bar.y : 0}
                          height={bar.height}
                          width={bar.width}
                          fill={getColor(bar)}
                          onClick={(event) => handleBar(event, bar)}
                          onMouseLeave={(event) => hideTooltipHandler()}
                          onMouseMove={(event) =>
                            showTooltipHandler(event, xScale, bar)
                          }
                          onTouchEnd={(event) => hideTooltipHandler()}
                          onTouchMove={(event) =>
                            showTooltipHandler(event, xScale, bar)
                          }
                        />
                      );
                    });
                  });
                }}
              </BarStack>

              <AxisLeft
                numTicks={3}
                scale={yScale}
                stroke="black"
                tickStroke="black"
                tickLabelProps={(value, index) => ({
                  fill: 'black',
                  fontSize: 11,
                  textAnchor: 'end',
                  dy: '0.33em',
                  dx: '-0.33em',
                })}
                tickComponent={({ formattedValue, ...tickProps }) => (
                  <text {...tickProps} fill="black">
                    ${formattedValue}
                  </text>
                )}
              />

              <AxisBottom
                hideTicks={true}
                scale={xScale ? xScale : 0}
                top={yMax ? yMax : 0}
                stroke="black"
                tickStroke="black"
                tickLabelProps={(value, index) => ({
                  fontSize: 11,
                  textAnchor: 'middle',
                  dy: '-0.5em',
                })}
                tickComponent={({ formattedValue, ...tickProps }) => (
                  <text
                    {...tickProps}
                    fill={
                      selectedBottomItems.find(item => item.month === parseInt(formattedValue))
                        ? activeLabelColor
                        : 'black'
                    }
                    onClick={(event) =>
                      handleLabel(event, parseInt(formattedValue))
                    }
                  >
                    {enMonths[formattedValue]}
                  </text>
                )}
              />
            </Group>
          </svg>

          {tooltipOpen && (
            <TooltipWithBounds
             
              top={tooltipTop + pageYOffset}
              left={tooltipLeft}
            >
              {tooltipData.isBar ? (
                <div className="color-black">
                  <div className="pdv-10">
                    <strong>
                      FY {tooltipData.key} :{' '}
                      {enMonths[x(tooltipData.bar.data)]}
                    </strong>
                  </div>
                  <div className="ft-12">
                    Change During Period:{' '}
                    <strong>
                      {thousandFormat2(tooltipData.bar.data[tooltipData.key])}
                    </strong>
                  </div>
                </div>
              ) : null}
            </TooltipWithBounds>
          )}
        </Grid>
      </div>
    </div>
  );
};

BottomChart.propTypes = {
  bottomData: PropTypes.array.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  selectedBottomItems: PropTypes.array.isRequired,
  updateFilter: PropTypes.func.isRequired,
  cashflowMiddleRequest: PropTypes.func.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
};

export default BottomChart;
